// Catalog.ID Client — Letterhead Background (pure)
// ============================================================================
// Builds the pdfmake `background` callback that paints the Catalog letterhead
// under every page. Asset-free on purpose: the SVG strings are injected by
// ../letterhead.ts (bundled via Vite `?raw`) or by tooling that reads the files
// from design/ directly, so this module — and everything that only needs the
// margins — stays importable outside the bundler.
//
// The artwork is full-page A4 (viewBox 0 0 595.276 841.89) and is drawn
// scaled to the actual page size, anchored at the page origin: the ISO A
// series shares the √2 aspect ratio, so the same file lands proportionally on
// A5 (~71%). Colour is the designer's; nothing here recolours.

export interface PageSizeLike {
    width: number;
    height: number;
}

export interface LetterheadSvgNode {
    svg: string;
    width: number;
    height: number;
    absolutePosition: { x: number; y: number };
}

export type LetterheadBackground = (currentPage: number, pageSize: PageSizeLike) => LetterheadSvgNode;

/**
 * 'stationery': first page carries header + footer, later pages footer only —
 * a multi-page letter. 'every-page': header + footer on all pages — for
 * documents whose pages are meant to be separated (the identity booklet).
 */
export type LetterheadMode = 'stationery' | 'every-page';

export function makeLetterheadBackground(
    firstPageSvg: string,
    continuedSvg: string,
    mode: LetterheadMode = 'stationery',
): LetterheadBackground {
    return (currentPage, pageSize) => ({
        svg: mode === 'every-page' || currentPage === 1 ? firstPageSvg : continuedSvg,
        width: pageSize.width,
        height: pageSize.height,
        absolutePosition: { x: 0, y: 0 },
    });
}

/** The kit cover artwork (design/coverpage.svg): the Catalog lockup alone on
 *  a white page — no header band, no footer band — painted under page 1 of a
 *  kit in place of the letterhead (booklet-definition's `coverBackground`).
 *  Full-page A4 like the letterhead, scaled and anchored the same way; the
 *  page number is ignored because the doc-definition already restricts it to
 *  page 1. Asset-free for the same reason as `makeLetterheadBackground`. */
export function makeCoverBackground(coverSvg: string): LetterheadBackground {
    return (_currentPage, pageSize) => ({
        svg: coverSvg,
        width: pageSize.width,
        height: pageSize.height,
        absolutePosition: { x: 0, y: 0 },
    });
}

// Measured ink bands of design/letterhead.svg on A4 (pt from the top edge,
// rasterised 2026-09-02): header 23–74, footer 789–821 (= 21–53 from the
// bottom). letterhead-continued.svg carries only the footer band, same place.
// Margins are [left, top, right, bottom]; the bottom margin also hosts the
// "page n of n" line pdfmake draws inside it, above the footer artwork.
//
// The top margin clears the header band by 26 pt rather than the strictly
// necessary 16 — review 2026-09-02 (third round) asked for more air under the
// letterhead. A5 is the A4 set scaled by 1/√2, as the artwork is.
export const LETTERHEAD_PAGE_MARGINS_A4: [number, number, number, number] = [60, 100, 60, 80];
export const LETTERHEAD_PAGE_MARGINS_A5: [number, number, number, number] = [42, 71, 42, 57];

/** Top margin for CONTINUATION pages under letterhead-continued.svg (round 5,
 *  2026-09-03): the continued stationery carries no header band at all, so the
 *  text may start much higher — this is what the pre-rendered agreements use
 *  for pages 2+. 50 pt ≈ the side margins, read as a deliberate document top. */
export const LETTERHEAD_CONTINUED_TOP_MARGIN_A4 = 50;
